import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useProducts } from '@/modules/products/hooks/useProducts';
import { usePlans } from '@/modules/plans/hooks/usePlans';
import { SubscriptionStatusBadge } from '@/modules/subscriptions/components/SubscriptionStatusBadge';
import { EntitlementStatusBadge } from '@/modules/trials/components/EntitlementStatusBadge';
import { TrialTruncatedText } from '@/modules/trials/components/TrialTruncatedText';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef } from '@/shared/components/ui/DataTable';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { Input } from '@/shared/components/ui/input';
import { formatMoney } from '@/shared/utils/money';
import { PaymentDetailDrawer } from '../components/PaymentDetailDrawer';
import { PaymentMobileCard } from '../components/PaymentMobileCard';
import { PaymentProviderBadge } from '../components/PaymentProviderBadge';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { listAdminPayments } from '../services/adminPayments.api';
import type { AdminPaymentItem, ListAdminPaymentsQuery } from '../types/adminPayment';
import { PAYMENT_PROVIDER_OPTIONS, PAYMENT_STATUS_OPTIONS } from '../types/adminPayment';
import {
  computePaymentKpis,
  formatDate,
  formatDateTime,
  formatProviderReference,
} from '../utils/paymentHelpers';

function KpiSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-5 animate-pulse">
      <div className="h-3 w-24 rounded bg-muted" />
      <div className="mt-3 h-8 w-16 rounded bg-muted" />
    </div>
  );
}

export function PaymentsListPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';
  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;

  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<AdminPaymentItem | null>(null);

  useEffect(() => {
    if (user && !isSuperAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [user, isSuperAdmin, navigate]);

  const serverQuery = useMemo((): ListAdminPaymentsQuery => {
    const q: ListAdminPaymentsQuery = { limit: 100 };
    if (statusFilter) q.status = statusFilter;
    if (providerFilter) q.provider = providerFilter;
    if (productFilter) q.product_id = productFilter;
    if (planFilter) q.plan_id = planFilter;
    if (dateFrom) {
      const start = new Date(`${dateFrom}T00:00:00`);
      q.from = start.toISOString();
    }
    if (dateTo) {
      const end = new Date(`${dateTo}T23:59:59.999`);
      q.to = end.toISOString();
    }
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    return q;
  }, [statusFilter, providerFilter, productFilter, planFilter, dateFrom, dateTo, debouncedSearch]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'payments', 'list', serverQuery],
    queryFn: () => listAdminPayments(serverQuery),
    enabled: isSuperAdmin,
  });

  const { data: productsData } = useProducts({ limit: 100 });
  const { data: plansData } = usePlans({ limit: 100 });

  const products = useMemo(() => productsData?.items ?? [], [productsData?.items]);
  const plans = useMemo(() => plansData?.items ?? [], [plansData?.items]);
  const payments = useMemo(() => data?.items ?? [], [data?.items]);
  const kpis = useMemo(() => computePaymentKpis(payments), [payments]);

  const applySearch = () => setDebouncedSearch(searchQuery);
  const unavailable = t('payments.identity.unavailable');

  const providerSplitLabel = useMemo(() => {
    const entries = Object.entries(kpis.providerSplit);
    if (entries.length === 0) return null;
    return entries.map(([label, count]) => `${label}: ${count}`).join(' · ');
  }, [kpis.providerSplit]);

  const columns: ColumnDef<AdminPaymentItem>[] = useMemo(
    () => [
      {
        key: 'customer',
        header: t('payments.columns.customer'),
        accessor: (payment) => (
          <div className="space-y-0.5 min-w-0">
            <TrialTruncatedText
              value={payment.customer_name}
              fallback={unavailable}
              maxWidthClass="max-w-[10rem] lg:max-w-[12rem]"
              className="font-medium"
            />
            <TrialTruncatedText
              value={payment.tenant_name}
              fallback={unavailable}
              maxWidthClass="max-w-[10rem] lg:max-w-[12rem]"
              className="text-xs text-muted-foreground"
            />
          </div>
        ),
        sortValue: (payment) => payment.customer_name ?? payment.tenant_name,
      },
      {
        key: 'product',
        header: t('payments.columns.product'),
        accessor: (payment) => (
          <TrialTruncatedText
            value={payment.product_name}
            fallback={unavailable}
            maxWidthClass="max-w-[8rem]"
          />
        ),
        sortValue: (payment) => payment.product_name,
      },
      {
        key: 'plan',
        header: t('payments.columns.plan'),
        accessor: (payment) => (
          <TrialTruncatedText
            value={payment.plan_name}
            fallback={unavailable}
            maxWidthClass="max-w-[8rem]"
          />
        ),
        sortValue: (payment) => payment.plan_name,
      },
      {
        key: 'provider',
        header: t('payments.provider'),
        accessor: (payment) => (
          <PaymentProviderBadge provider={payment.provider} label={payment.provider_label} />
        ),
        sortValue: (payment) => payment.provider_label,
        align: 'center',
      },
      {
        key: 'status',
        header: t('payments.status.label'),
        accessor: (payment) => <PaymentStatusBadge status={payment.status} />,
        sortValue: (payment) => payment.status,
        align: 'center',
      },
      {
        key: 'amount',
        header: t('payments.amount'),
        accessor: (payment) => (
          <span className="font-mono tabular-nums whitespace-nowrap">
            {formatMoney(payment.amount, payment.currency)}
          </span>
        ),
        sortValue: (payment) => payment.amount,
        align: 'right',
      },
      {
        key: 'subscriptionStatus',
        header: t('payments.columns.subscriptionStatus'),
        accessor: (payment) => <SubscriptionStatusBadge status={payment.subscription_status} />,
        sortValue: (payment) => payment.subscription_status,
        align: 'center',
      },
      {
        key: 'entitlement',
        header: t('payments.columns.entitlementStatus'),
        accessor: (payment) => <EntitlementStatusBadge status={payment.entitlement_status} />,
        sortValue: (payment) => payment.entitlement_status ?? '',
        align: 'center',
      },
      {
        key: 'providerRef',
        header: t('payments.columns.providerReference'),
        accessor: (payment) => (
          <TrialTruncatedText
            value={formatProviderReference(payment)}
            fallback={unavailable}
            maxWidthClass="max-w-[9rem]"
            nowrap
            className="font-mono text-xs"
          />
        ),
        sortValue: (payment) => formatProviderReference(payment) ?? '',
      },
      {
        key: 'created',
        header: t('payments.columns.created'),
        accessor: (payment) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatDate(payment.created_at, locale)}
          </span>
        ),
        sortValue: (payment) => payment.created_at,
      },
      {
        key: 'processed',
        header: t('payments.columns.processed'),
        accessor: (payment) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap tabular-nums">
            {formatDateTime(payment.processed_at, locale)}
          </span>
        ),
        sortValue: (payment) => payment.processed_at ?? '',
      },
      {
        key: 'actions',
        header: t('payments.columns.actions'),
        accessor: (payment) => (
          <div className="flex justify-end whitespace-nowrap shrink-0">
            <Button variant="outline" size="sm" onClick={() => setSelectedPayment(payment)}>
              <Icons.Eye className="h-3.5 w-3.5" />
              {t('common.view')}
            </Button>
          </div>
        ),
        align: 'right',
      },
    ],
    [t, locale, unavailable],
  );

  if (!user || !isSuperAdmin) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHero
          title={t('payments.title')}
          subtitle={t('payments.description')}
          icon={<Icons.DollarSign className="h-7 w-7" />}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <KpiSkeleton key={i} />
          ))}
        </div>
        <LoadingPanel />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHero
          title={t('payments.title')}
          subtitle={t('payments.description')}
          icon={<Icons.DollarSign className="h-7 w-7" />}
        />
        <ErrorPanel message={t('payments.error.loadFailed')} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHero
        title={t('payments.title')}
        subtitle={t('payments.description')}
        eyebrow={t('payments.eyebrow')}
        icon={<Icons.DollarSign className="h-7 w-7" />}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link
              to="/dashboard/payments/instapay"
              className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-accent/10"
            >
              {t('payments.instapay.linkFromPayments')}
            </Link>
            <Link
              to="/dashboard/payments/myfatoorah"
              className="inline-flex h-8 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-accent/10"
            >
              {t('payments.myfatoorah.linkFromPayments')}
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <Icons.RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </Button>
          </div>
        }
      />

      <KpiGrid className="gap-4 xl:grid-cols-3 2xl:grid-cols-6">
        <KpiCard title={t('payments.kpi.total')} value={String(kpis.total)} tone="blue" />
        <KpiCard title={t('payments.kpi.succeeded')} value={String(kpis.succeeded)} tone="green" />
        <KpiCard title={t('payments.kpi.pending')} value={String(kpis.pending)} tone="amber" />
        <KpiCard title={t('payments.kpi.failed')} value={String(kpis.failed)} tone="rose" />
        <KpiCard title={t('payments.kpi.refunded')} value={String(kpis.refunded)} tone="violet" />
        <KpiCard
          title={t('payments.kpi.totalAmount')}
          value={
            kpis.totalAmountMinor !== null && kpis.totalAmountCurrency
              ? formatMoney(kpis.totalAmountMinor, kpis.totalAmountCurrency)
              : '—'
          }
          hint={
            kpis.totalAmountMinor === null
              ? t('payments.kpi.mixedCurrencyHint')
              : t('payments.kpi.loadedResultsNote')
          }
          tone="cyan"
        />
      </KpiGrid>

      {providerSplitLabel ? (
        <p className="text-xs text-muted-foreground">
          {t('payments.kpi.providerSplit', { split: providerSplitLabel })}
        </p>
      ) : null}

      <div className="rounded-xl border border-border bg-card p-4 space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1.5 md:col-span-2">
            <label
              htmlFor="payment-search-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('payments.filters.search')}
            </label>
            <div className="flex gap-2">
              <Input
                id="payment-search-filter"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('payments.filters.searchPlaceholder')}
                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              />
              <Button variant="secondary" onClick={applySearch}>
                {t('payments.filters.searchApply')}
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="payment-status-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('payments.status.label')}
            </label>
            <select
              id="payment-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('payments.allStatuses')}</option>
              {PAYMENT_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`payments.status.${s}`, s)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="payment-provider-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('payments.provider')}
            </label>
            <select
              id="payment-provider-filter"
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('payments.filters.allProviders')}</option>
              {PAYMENT_PROVIDER_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {t(`payments.providers.${p}`, p)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="payment-product-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('payments.filters.product')}
            </label>
            <select
              id="payment-product-filter"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('payments.filters.allProducts')}</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {i18n.language === 'ar' ? product.name_ar || product.name_en : product.name_en}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="payment-plan-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('payments.filters.plan')}
            </label>
            <select
              id="payment-plan-filter"
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">{t('payments.filters.allPlans')}</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {i18n.language === 'ar' ? plan.name_ar || plan.name_en : plan.name_en}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="payment-date-from-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('payments.filters.dateFrom')}
            </label>
            <Input
              id="payment-date-from-filter"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="payment-date-to-filter"
              className="text-xs font-medium text-muted-foreground"
            >
              {t('payments.filters.dateTo')}
            </label>
            <Input
              id="payment-date-to-filter"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{t('payments.filters.hint')}</p>
      </div>

      {payments.length === 0 ? (
        <EmptyPanel title={t('payments.empty.title')} message={t('payments.empty.message')} />
      ) : (
        <>
          <div className="hidden md:block">
            <DataTable<AdminPaymentItem>
              data={payments}
              columns={columns}
              rowKey={(payment) => payment.payment_id}
              total={data?.total ?? payments.length}
              initialPageSize={10}
              emptyMessage={t('payments.empty.title')}
            />
          </div>
          <div className="md:hidden space-y-3">
            {payments.map((payment) => (
              <PaymentMobileCard
                key={payment.payment_id}
                payment={payment}
                locale={locale}
                onView={() => setSelectedPayment(payment)}
              />
            ))}
          </div>
        </>
      )}

      <p className="text-xs text-muted-foreground">
        {t('payments.footer.showing', {
          shown: payments.length,
          total: data?.total ?? payments.length,
        })}
      </p>

      {selectedPayment ? (
        <PaymentDetailDrawer payment={selectedPayment} onClose={() => setSelectedPayment(null)} />
      ) : null}
    </div>
  );
}
